<template>
    <div>
        <AlertLayout v-if="currentTab === 'user-management' || currentTab === 'role-management'" title="Reminders!"
            message="Only registered officers will be found below. You may register new officer here.<br>Click on Details button for more information on the officer of choice"
            type="warning" />
        <AlertLayout v-if="currentTab === 'facility-management'" title="Reminders!"
            message="Only registered facilities will be found below. You may register new facility here.<br>Click on Details button for more information on the facility of choice"
            type="warning" />
        <AlertLayout v-if="currentTab === 'feature-flagging'" title="Reminders!"
            message="This page lists and manages feature flagging to specified facilities." type="warning" />

        <div class="flex flex-wrap items-center justify-between py-1 pb-4">
            <div class="flex items-center space-x-4">
                <div class="relative">
                    <button id="dropdownRadioButton"
                        class="inline-flex items-center px-6 py-2 text-gray-400 bg-white border border-gray-300 rounded-md"
                        type="button" @click="toggleDropdown">
                        <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24"
                            height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-width="2"
                                d="M18.796 4H5.204a1 1 0 0 0-.753 1.659l5.302 6.058a1 1 0 0 1 .247.659v4.874a.5.5 0 0 0 .2.4l3 2.25a.5.5 0 0 0 .8-.4v-7.124a1 1 0 0 1 .247-.659l5.302-6.059c.566-.646.106-1.658-.753-1.658Z" />
                        </svg>
                        Filters
                        <svg class="w-2.5 h-2.5 ms-2.5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
                            fill="none" viewBox="0 0 10 6">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="m1 1 4 4 4-4" />
                        </svg>
                    </button>

                    <!-- Filter dropdown -->
                    <div v-show="isDropdownOpen" id="filterDropdown"
                        class="absolute z-10 p-4 mt-2 bg-white border border-gray-300 rounded-lg shadow-lg w-80">
                        <form @submit.prevent="applyFilters">
                            <div class="mb-4">
                                <label for="contactMode" class="block mb-2 text-sm font-medium text-gray-900">Contact
                                    Mode</label>
                                <select id="contactMode" v-model="filters.contactMode"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                                    <option value="">All</option>
                                    <option value="Email">Email</option>
                                    <option value="Phone">Phone</option>
                                    <option value="Both">Both</option>
                                </select>
                            </div>
                            <div class="mb-4">
                                <label for="department"
                                    class="block mb-2 text-sm font-medium text-gray-900">Department</label>
                                <select id="department" v-model="filters.department"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                                    <option value="">All</option>
                                    <option v-for="dept in departments" :key="dept.department_id" :value="dept.department_id">
                                        {{ dept.name }}
                                    </option>
                                </select>
                            </div>
                            <div class="mb-4">
                                <label for="registrationDate"
                                    class="block mb-2 text-sm font-medium text-gray-900">Registration Date</label>
                                <input type="date" id="registrationDate" v-model="filters.registrationDate"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                            </div>
                            <div class="mb-4">
                                <label for="status" class="block mb-2 text-sm font-medium text-gray-900">Status</label>
                                <select id="status" v-model="filters.status"
                                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg block w-full p-2.5">
                                    <option value="">All</option>
                                    <option value="Active">Active</option>
                                    <option value="Inactive">Inactive</option>
                                </select>
                            </div>
                            <div class="flex justify-end space-x-2">
                                <button type="button" @click="cancelFilters"
                                    class="px-4 py-2 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg hover:bg-gray-100">
                                    Clear
                                </button>
                                <button type="submit"
                                    class="px-4 py-2 text-sm font-medium text-white rounded-lg bg-custom-yellow hover:bg-custom-yellow-dark">
                                    Apply Filters
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
                <!-- Search input -->
                <div class="relative flex">
                    <input type="text" v-model="searchQuery" id="table-search"
                        class="px-4 py-2 text-gray-800 border border-gray-300 rounded-md dark:border-gray-300 dark:placeholder-gray-400"
                        placeholder="Search" />
                </div>
            </div>
            <div class="flex items-center mt-4 space-x-4 sm:mt-0">
                <template v-if="currentTab === 'user-management'">
                    <CustomButton label="New Officer" :icon-path="'M5 12h14m-7 7V5'" bg-class="bg-custom-yellow"
                        @click="handleNewUser" />
                </template>
                <template v-if="currentTab === 'facility-management'">
                    <CustomButton label="New Facility" :icon-path="'M5 12h14m-7 7V5'" bg-class="bg-custom-yellow"
                        @click="handleNewFacility" />
                </template>
                <template v-if="currentTab === 'role-management'">
                    <CustomButton label="New Role" :icon-path="'M5 12h14m-7 7V5'" bg-class="bg-custom-yellow"
                        @click="handleNewRole" />
                </template>
                <template v-if="currentTab === 'feature-flagging'">
                    <CustomButton label="New Feature" :icon-path="'M5 12h14m-7 7V5'" bg-class="bg-custom-yellow"
                        @click="openFeatureModal" />
                </template>
            </div>
            <div v-if="isPageLoading"
                class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div class="w-12 h-12 border-t-4 border-b-4 border-yellow-500 rounded-full animate-spin"></div>
            </div>
        </div>
        <div class="flex mb-4 border-b tabs">
            <button class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
                :class="{ 'border-custom-yellow': currentTab === 'user-management' }"
                @click="currentTab = 'user-management'">
                Users
            </button>
            <button v-if="hasManageClaim('Manage Client')"
                class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
                :class="{ 'border-custom-yellow': currentTab === 'facility-management' }"
                @click="currentTab = 'facility-management'">
                Facility
            </button>
            <button v-if="hasManageClaim('Manage Users')"
                class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
                :class="{ 'border-custom-yellow': currentTab === 'role-management' }"
                @click="currentTab = 'role-management'">
                Role Management
            </button>
            <button v-if="hasManageClaim('Manage Client')"
                class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
                :class="{ 'border-custom-yellow': currentTab === 'feature-flagging' }"
                @click="currentTab = 'feature-flagging'">
                Feature Flagging
            </button>
            <button v-if="hasManageClaim('Manage Client')"
                class="px-4 py-2 -mb-px font-semibold text-gray-500 border-b-2"
                :class="{ 'border-custom-yellow': currentTab === 'manuals-management' }"
                @click="currentTab = 'manuals-management'">
                Manuals
            </button>
        </div>
        <component :is="currentTabComponent" ref="currentTabComponent" :search-query="searchQuery"></component>
    </div>

    <div v-if="showFeatureModal"
        class="fixed inset-0 bg-gray-900 bg-opacity-75 flex justify-center items-center z-50 backdrop-blur-sm">
        <div class="bg-white p-8 rounded-lg shadow-xl w-1/2 mt-4 mb-4">
            <h2 class="text-2xl text-gray-800 font-bold mb-4">Add New Feature</h2>
            <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200">
            <form @submit.prevent="handleCreateFeature">
                <div class="mb-4">
                    <label for="featureName" class="block mb-2 text-sm font-medium text-gray-900">Feature Name</label>
                    <input type="text" id="featureName" v-model="newFeature.name" required
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-md block w-full py-2 px-3 dark:placeholder-gray-400"
                        placeholder="Enter feature name" />
                </div>
                <div class="mb-4">
                    <label for="featureDescription" class="block mb-2 text-sm font-medium text-gray-900">Description</label>
                    <textarea id="featureDescription" v-model="newFeature.description" rows="3"
                        class="bg-gray-50 border border-gray-300 text-gray-900 rounded-md block w-full py-2 px-3 dark:placeholder-gray-400"
                        placeholder="Enter feature description"></textarea>
                </div>
                <div class="flex justify-end space-x-2 mt-4">
                    <button type="button" @click="closeFeatureModal"
                        class="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
                    <button type="submit"
                        class="px-4 py-2 bg-custom-yellow text-white rounded">Create Feature</button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import AlertLayout from "@/components/AlertLayout.vue";
import CustomButton from "@/components/CustomButton.vue";
import UserManagementTab from "../tabs/UserManagementTab.vue";
import FacilityManagementTab from "../tabs/FacilityManagementTab.vue";
import RoleManagementTab from "../tabs/RoleManagementTab.vue";
import FeatureFlaggingTab from "../tabs/FeatureFlaggingTab.vue";
import ManualsManagementTab from "../tabs/ManualsManagementTab.vue";
import api from "@/services/api";
import SweetAlertService from '@/services/SweetAlertService';

export default {
    name: "UserManagementPage",
    components: {
        AlertLayout,
        CustomButton,
        UserManagementTab,
        FacilityManagementTab,
        RoleManagementTab,
        FeatureFlaggingTab,
        ManualsManagementTab
    },
    
    data() {
        return {
            currentTab: "user-management",
            searchQuery: "",
            isDropdownOpen: false,
            isPageLoading: false,
            user: [],
            departments: [],
            filters: {
                contactMode: '',
                facility: '',
                department: '',
                registrationDate: '',
                status: ''
            },
            showFeatureModal: false,
            newFeature: {
                name: '',
                description: ''
            }
        };
    },
    created() {
        // Restore the currentTab from localStorage if it exists
        const savedTab = localStorage.getItem('userManagementPageTab');
        if (savedTab && [
            'user-management',
            'facility-management',
            'role-management',
            'feature-flagging',
            'manuals-management'
        ].includes(savedTab)) {
            this.currentTab = savedTab;
        }
    },
    watch: {
        // Watch for changes to currentTab and save to localStorage
        currentTab(newTab) {
            localStorage.setItem('userManagementPageTab', newTab);
        }
    },
    computed: {
        currentTabComponent() {
            switch (this.currentTab) {
                case "user-management":
                    return "UserManagementTab";
                case "facility-management":
                    return "FacilityManagementTab";
                case "role-management":
                    return "RoleManagementTab";
                case "feature-flagging":
                    return "FeatureFlaggingTab";
                case "manuals-management":
                    return "ManualsManagementTab";
                default:
                    return "UserManagementTab";
            }
        },
    },

    methods: {
        async fetchDepartments() {
            try {
                const response = await api.getDepartments();
                const list = response.data || response || [];
                this.departments = (Array.isArray(list) ? list : [])
                    .filter((dept) => dept && dept.department_id && dept.name)
                    .sort((a, b) => a.name.localeCompare(b.name));
            } catch {
                this.departments = [];
            }
        },
        async handleNewUser() {
            this.isPageLoading = true;
            try {
                await new Promise(resolve => setTimeout(resolve, 500));
                this.$router.push({ name: 'NewUserForm' });
            } catch {
                this.isPageLoading = false;
            }
        },
        async handleNewFacility() {
            this.isPageLoading = true;
            try {
                await new Promise(resolve => setTimeout(resolve, 500));
                this.$router.push({ name: 'NewFacilityForm' });
            } catch {
                this.isPageLoading = false;
            }
        },
        async handleNewRole() {
            this.isPageLoading = true;
            try {
                await new Promise(resolve => setTimeout(resolve, 500));
                this.$router.push({ name: 'NewRoleForm' });
            } catch {
                this.isPageLoading = false;
            }
        },
        openFeatureModal() {
            this.newFeature = { name: '', description: '' };
            this.showFeatureModal = true;
        },
        closeFeatureModal() {
            this.showFeatureModal = false;
        },
        async handleCreateFeature() {
            this.isPageLoading = true;
            try {
                await api.createFeature(this.newFeature);
                this.closeFeatureModal();
                await SweetAlertService.showSuccess('Success!', 'Feature created successfully!');
                // Refresh features in the tab
                if (this.$refs.currentTabComponent && this.$refs.currentTabComponent.fetchFeatures) {
                    this.$refs.currentTabComponent.fetchFeatures();
                }
            } catch (error) {
                console.error("Failed to create feature:", error);
                SweetAlertService.showError('Error', 'Failed to create feature.');
            } finally {
                this.isPageLoading = false;
            }
        },
        toggleDropdown() {
            this.isDropdownOpen = !this.isDropdownOpen;
        },

        applyFilters() {
            console.log('Applying filters:', this.filters);
            this.applyFiltersToCurrentTab();
            this.isDropdownOpen = false;
        },

        applyFiltersToCurrentTab() {
            if (this.$refs.currentTabComponent && typeof this.$refs.currentTabComponent.applyFilters === 'function') {
                this.$refs.currentTabComponent.applyFilters(this.filters);
            } else {
                console.error('applyFilters method not found on current tab component');
            }
        },

        cancelFilters() {
            this.filters = {
                contactMode: '',
                facility: '',
                department: '',
                registrationDate: '',
                status: ''
            };
            this.applyFilters();
        },

        hasManageClaim(value) {
            const claims = this.user?.user_roles?.[0]?.role?.claims || [];
            return claims.some((claim) => claim.name === value);
        },
    },
    mounted() {
        this.user = this.$store.getters["auth/user"];
        this.fetchDepartments();
    },
};

</script>

<style lang="css" scoped></style>